import { useState } from 'react';
import ProductCard from './ProductCard';
import { Product } from '@/types';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ProductGridProps {
  products: Product[];
  searchQuery: string;
}

const ProductGrid = ({ products, searchQuery }: ProductGridProps) => {
  const [sortOption, setSortOption] = useState<string>('expirationDate');
  const [filterOption, setFilterOption] = useState<string>('all');

  // Filter products based on search query and filter option
  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.ipAddress.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Update filter logic to check expiration date for status
    const today = new Date();
    const expirationDate = new Date(product.expirationDate);
    const isExpired = expirationDate < today;
    
    if (filterOption === 'all') return matchesSearch;
    if (filterOption === 'active') return matchesSearch && !isExpired;
    if (filterOption === 'expired') return matchesSearch && isExpired;
    return matchesSearch;
  });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortOption) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'expirationDate':
        return new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime();
      case 'recentlyAdded':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'startDate':
        return new Date(a.licenseStartDate).getTime() - new Date(b.licenseStartDate).getTime();
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex space-x-2">
          <Button 
            variant={filterOption === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterOption('all')}
            size="sm"
          >
            Tous
          </Button>
          <Button 
            variant={filterOption === 'active' ? 'default' : 'outline'}
            onClick={() => setFilterOption('active')}
            size="sm"
          >
            Actifs
          </Button>
          <Button 
            variant={filterOption === 'expired' ? 'default' : 'outline'}
            onClick={() => setFilterOption('expired')}
            size="sm"
          >
            Expirés
          </Button>
        </div>
        <div className="w-full sm:w-48">
          <Select value={sortOption} onValueChange={setSortOption}>
            <SelectTrigger>
              <SelectValue placeholder="Trier par" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="expirationDate">Date d'expiration</SelectItem>
              <SelectItem value="startDate">Date de début</SelectItem>
              <SelectItem value="name">Nom</SelectItem>
              <SelectItem value="recentlyAdded">Récemment ajouté</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-lg text-muted-foreground">Aucun produit disponible</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedProducts.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
